import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  Slider,
  Switch,
  TextField,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { addDays, addMinutes, formatDuration } from 'date-fns';
import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { translations } from 'locales/i18n';
import { Article } from 'types/AppTypes';

interface ValueLabelProps {
  children: React.ReactElement;
  open: boolean;
  value: number;
}

export default function Lifetime() {
  const { t } = useTranslation();
  const { setFieldValue, values, errors } = useFormikContext<Article>();

  const handleStartDateChange = (e: Date | null) => {
    const startDate = e;
    setFieldValue('appointment.startDate', startDate);
    if (!values.appointment.endDate || !startDate) {
      return;
    }
    if (!values.appointment.allDay) {
      setFieldValue('appointment.endDate', addMinutes(startDate, values.appointment.duration ?? 0));
      return;
    }
    if (values.appointment.allDay && values.appointment.endDate < startDate) {
      setFieldValue('appointment.endDate', startDate);
    }
  };

  const ValueLabelComponent = (props: ValueLabelProps) => {
    const { children, open, value } = props;

    return (
      <Tooltip
        open={open}
        enterTouchDelay={0}
        placement="top"
        title={value}
        classes={{ tooltip: 'articleModal__body__input__tooltip' }}
      >
        {children}
      </Tooltip>
    );
  };

  const sliderMarks: Array<{ value: number; label: string }> = [];
  for (let i = 1; i < 9; i++) {
    sliderMarks.push({
      value: i * 60,
      label: `${i}h`
    });
  }

  return (
    <Grid container>
      {values.type !== 'event' && values.type !== 'holiday' && values.type !== 'legal' && (
        <Grid container>
          <FormControl className="articleModal__body__input" required fullWidth>
            <FormControlLabel
              control={
                <Switch
                  checked={values.withAppointment}
                  onChange={v => {
                    setFieldValue('withAppointment', v.target.checked);
                  }}
                  name="withAppointment"
                  color="primary"
                />
              }
              label={t(translations.pages.articles.create.lifetime.withAppointment()) as string}
            />
            <FormHelperText className="articleModal__body__input__helper">
              {t(translations.pages.articles.create.lifetime.withAppointmentDescription())}
            </FormHelperText>
          </FormControl>
        </Grid>
      )}
      {(values.type === 'event' ||
        values.type === 'holiday' ||
        values.type === 'legal' ||
        values.withAppointment) && (
        <Grid container>
          <FormControl className="articleModal__body__input" required fullWidth>
            <FormLabel className="articleModal__body__input__label">
              {t(translations.pages.articles.create.lifetime.eventDate())}
            </FormLabel>
            {values.type === 'event' || values.type === 'legal' ? (
              <FormHelperText className="articleModal__body__input__helper">
                {t(translations.pages.articles.create.lifetime.eventDateDescription())}
              </FormHelperText>
            ) : values.type === 'holiday' ? (
              <FormHelperText className="articleModal__body__input__helper">
                {t(translations.pages.articles.create.lifetime.holidayDateDescription())}
              </FormHelperText>
            ) : null}
            <Grid item>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.appointment.allDay}
                    disabled={values.type === 'holiday'}
                    onChange={v => {
                      setFieldValue('appointment.allDay', v.target.checked);
                      setFieldValue(
                        'appointment.endDate',
                        addDays(new Date(values.appointment.startDate), 1)
                      );
                      if (v.target.checked) {
                        setFieldValue('appointment.duration', 0);
                      } else {
                        setFieldValue('appointment.duration', 30);
                      }
                    }}
                    name="appointment.allDay"
                    color="primary"
                  />
                }
                label={t(translations.pages.articles.create.lifetime.wholeDay()) as string}
              />
            </Grid>
          </FormControl>
          {values.appointment.allDay ? (
            <Grid container spacing={4}>
              <Grid item xs={12} sm="auto">
                <DatePicker
                  className="articleModal__body__input__datepicker"
                  disablePast
                  renderInput={props => (
                    <TextField
                      {...props}
                      required
                      variant="outlined"
                      label={t(translations.pages.articles.create.lifetime.eventDate())}
                    />
                  )}
                  inputFormat="dd.MM.yyyy"
                  value={values.appointment.startDate}
                  onChange={handleStartDateChange}
                  minDate={new Date()}
                  // PopoverProps={{
                  //   anchorOrigin: {
                  //     vertical: 'center',
                  //     horizontal: 'right'
                  //   },
                  //   transformOrigin: {
                  //     vertical: 'center',
                  //     horizontal: 'left'
                  //   },
                  //   style: { marginLeft: '2em' }
                  // }}
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <DatePicker
                  className="articleModal__body__input__datepicker"
                  disablePast
                  renderInput={props => (
                    <TextField
                      {...props}
                      variant="outlined"
                      label={t(translations.pages.articles.create.lifetime.eventDateEnd())}
                    />
                  )}
                  inputFormat="dd.MM.yyyy"
                  value={values.appointment.endDate}
                  onChange={e => setFieldValue('appointment.endDate', e)}
                  minDate={values.appointment.startDate}
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container className="articleModal__body__input" spacing={4}>
              <Grid item xs={12} sm={6} md="auto">
                <DateTimePicker
                  className="articleModal__body__input__datepicker"
                  disablePast
                  renderInput={props => (
                    <TextField
                      {...props}
                      required
                      variant="outlined"
                      label={t(translations.pages.articles.create.lifetime.eventDate())}
                    />
                  )}
                  inputFormat="dd.MM.yyyy HH:mm"
                  value={values.appointment.startDate}
                  onChange={handleStartDateChange}
                  ampm={false}
                  // PopoverProps={{
                  //   anchorOrigin: {
                  //     vertical: 'center',
                  //     horizontal: 'right'
                  //   },
                  //   transformOrigin: {
                  //     vertical: 'center',
                  //     horizontal: 'left'
                  //   },
                  //   style: { marginLeft: '2em' }
                  // }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md>
                <Grid container style={{ marginTop: '0.5em' }}>
                  <InputLabel shrink required>
                    {t(translations.pages.articles.create.lifetime.eventDuration())}
                  </InputLabel>
                </Grid>
                <Slider
                  value={values.appointment.duration}
                  valueLabelDisplay="auto"
                  valueLabelFormat={v => {
                    return formatDuration({ hours: Math.floor(v / 60), minutes: v % 60 });
                  }}
                  components={{ ValueLabel: ValueLabelComponent }}
                  step={15}
                  marks={sliderMarks}
                  min={15}
                  max={480}
                  onChange={(e, v) => setFieldValue('appointment.duration', v)}
                />
              </Grid>
            </Grid>
          )}
          <Grid container>
            <Field
              as={TextField}
              name="appointment.venue"
              variant="outlined"
              error={errors.appointment?.venue}
              helperText={errors.appointment?.venue}
              label={t(translations.pages.articles.create.lifetime.venue())}
              style={{ marginTop: '1em' }}
              fullWidth
            />
          </Grid>
        </Grid>
      )}
      <Grid container>
        <FormControl className="articleModal__body__input" fullWidth>
          <FormLabel className="articleModal__body__input__label">
            {t(translations.pages.articles.create.lifetime.lifetime())}
          </FormLabel>
          {!['event', 'legal', 'holiday'].includes(values.type) && (
            <FormHelperText className="articleModal__body__input__helper">
              {t(translations.pages.articles.create.lifetime.lifetimeDescription())}
            </FormHelperText>
          )}
        </FormControl>
        <Grid container>
          <Grid item xs={12} sm={6}>
            <DatePicker
              className="articleModal__body__input__datepicker"
              renderInput={props => (
                <TextField
                  {...props}
                  variant="outlined"
                  label={t(translations.pages.articles.create.lifetime.startDate())}
                />
              )}
              inputFormat="dd.MM.yyyy"
              value={values.startDate}
              onChange={e => e && setFieldValue('startDate', e)}
              maxDate={values.endDate || undefined}
            />
          </Grid>
          {values.type === 'event' || values.type === 'holiday' || values.type === 'legal' ? (
            <Grid container>
              <FormHelperText>
                {t(translations.pages.articles.create.lifetime.lifetimeDescriptionEvent())}
              </FormHelperText>
            </Grid>
          ) : (
            <Grid item xs={12} sm={6}>
              <DatePicker
                className="articleModal__body__input__datepicker"
                disablePast
                renderInput={props => (
                  <TextField
                    {...props}
                    variant="outlined"
                    label={t(translations.pages.articles.create.lifetime.endDate())}
                  />
                )}
                inputFormat="dd.MM.yyyy"
                value={values.endDate}
                onChange={e => e && setFieldValue('endDate', e)}
                minDate={values.startDate}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
